import { action, computed, observable, toJS } from 'mobx'
import lodash from 'lodash'
import { BindAll } from 'lodash-decorators'
import type { Dictionary } from 'vue-router/types/router'
import { $service } from '@xt/client/experimental/request/service'
import { reportEntryLog } from '@xt/client/utils/log'
import { myCourseMenu } from '@xt/client/dataSource/my/index'
import { EnumApiMy } from '@xt/client/api/my'
import { EnumMyCourseMenu } from '@xt/client/enums/my'
import { EnumProductTypeQuery, EnumSearchProductType } from '@xt/client/enums/product'
import type { SearchForm, CourseCardUIType } from './types.d'
import type { CourseItem, CourseType, MyCourseList } from '@xt/client/types/my-go-course'
import { transfromProductList } from '@xt/client/utils/my'

export type RouteQuery = Dictionary<string | (string | null)[]>

type MyProductStatus = {
  loading: boolean
  isError: boolean
  haveNoneCourse: boolean
}

@BindAll()
export class MyCourseController {
  @observable myProductList: Array<CourseItem & CourseCardUIType> = []
  @observable myProductStatus: MyProductStatus = {
    loading: true,
    isError: false,
    haveNoneCourse: false
  }
  @observable filterList: Array<CourseItem & CourseCardUIType> = []

  async loadMyProductList() {
    try {
      this.setMyProductStatus({
        loading: true
      })

      const [err, res] = await $service<MyCourseList>({
        url: EnumApiMy.MyCourseList,
        params: {
          all: true
        }
      })

      if (err) throw err

      this.setMyProductList([...transfromProductList(res)])

      this.setMyProductStatus({
        haveNoneCourse: this.myProductList.length === 0,
        isError: false
      })
    } catch (error) {
      reportEntryLog('toclass.loading-data.fail', { error })
      this.setMyProductStatus({
        isError: true
      })
    } finally {
      this.setMyProductStatus({
        loading: false
      })
    }
  }

  // 我的课程列表分类处理
  changeFilterList(filter: { menuType: string; courseType: string }) {
    const { menuType, courseType } = filter

    const list = lodash.filter(toJS(this.myProductList), item => {
      if (!menuType) {
        return this.mapCourseType(courseType, item)
      }
      if (!courseType) {
        return this.mapMenuType(menuType as EnumMyCourseMenu, item.type)
      }
      return this.mapProductType(courseType, item.type)
    })

    this.setFilterList(list)
  }
  mapMenuType(menuType: EnumMyCourseMenu, type: CourseType) {
    let filter: CourseType[] = []

    switch (menuType) {
      case EnumMyCourseMenu.Teach:
        filter = [EnumProductTypeQuery.Course, EnumProductTypeQuery.SpecialCourse]
        break
      case EnumMyCourseMenu.Product:
        filter = [EnumProductTypeQuery.DayCourse, EnumProductTypeQuery.SelfStudy, EnumProductTypeQuery.Exhibition]
        break
      case EnumMyCourseMenu.Offline:
        break
      default:
        break
    }

    return filter.includes(type)
  }
  mapCourseType(courseType: string, item: CourseItem) {
    if (!courseType) return true

    return item.contentType.toString() === courseType
  }
  mapProductType(courseType: string, type: CourseType) {
    switch (Number(courseType)) {
      case EnumSearchProductType.Series:
        return type === EnumProductTypeQuery.Course
      case EnumSearchProductType.SpecialTopic:
        return type === EnumProductTypeQuery.SpecialCourse
      case EnumSearchProductType.DayCourse:
        return type === EnumProductTypeQuery.DayCourse
      case EnumSearchProductType.SelfStudy:
        return type === EnumProductTypeQuery.SelfStudy
      case EnumSearchProductType.StudyExhibition:
        return type === EnumProductTypeQuery.Exhibition
      default:
        return false
    }
  }

  @action
  setMyProductStatus(status: Partial<MyProductStatus>) {
    this.myProductStatus = {
      ...this.myProductStatus,
      ...status
    }
  }

  @action
  setFilterList(list: Array<CourseItem & CourseCardUIType>) {
    this.filterList = [...list]
  }

  @action
  setMyProductList(list: Array<CourseItem & CourseCardUIType>) {
    this.myProductList = [...list]
  }

  getSeachListByFieldName(fieldName: keyof SearchForm, routerQuery: RouteQuery) {
    switch (fieldName) {
      case 'menuType':
        return myCourseMenu
      case 'courseType':
        const parseField = this.getFieldParser(routerQuery)
        return myCourseMenu.find(item => item.value === parseField('menuType'))?.filters || []
      default:
        return []
    }
  }

  getFieldParser(routerQuery: Dictionary<string | (string | null)[]>) {
    return (fieldName: keyof SearchForm, defultValue: string = '') => {
      const fieldValue = routerQuery[fieldName]

      if (typeof fieldValue !== 'string') return ''

      return this.getSeachListByFieldName(fieldName, routerQuery).find(i => i.value.toString() === fieldValue)?.value || defultValue
    }
  }

  // 获取路由参数
  public parserQuery(routerQuery: Dictionary<string | (string | null)[]>): SearchForm {
    const parseField = this.getFieldParser(routerQuery)

    return {
      menuType: parseField('menuType'),
      courseType: parseField('courseType')
    }
  }

  // 获取查询query值
  public toSearchQuery(form: SearchForm): Record<keyof SearchForm, string> {
    const obj = {} as Record<keyof SearchForm, string>

    Object.keys(form).forEach(key => {
      if (form[key].toString() !== '') {
        obj[key] = form[key].toString()
      }
    })

    return obj
  }

  @action
  reset() {
    this.myProductList = []
    this.filterList = []
    this.myProductStatus = {
      loading: true,
      isError: false,
      haveNoneCourse: false
    }
  }
}
